<template>
    <div class="tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-overflow-hidden tw-mb-4 tw-mt-3">
        <!-- Collapsible header -->
        <div @click="isOpen = !isOpen" class="tw-bg-gray-50 tw-px-4 tw-py-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex tw-items-center tw-gap-2">
                <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">{{ t('common.offers') }}</h2>
                <span v-if="availableOffers.length > 0" class="tw-bg-primary-100 tw-text-primary-600 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">{{ availableOffers.length }}</span>
                <span v-else-if="productId && !loading" class="tw-bg-orange-100 tw-text-orange-600 tw-text-xs tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full">0</span>
            </div>
            <icon :icon="isOpen ? 'heroicons-outline:chevron-up' : 'heroicons-outline:chevron-down'" class="tw-text-gray-500 tw-text-lg" />
        </div>
        
        <!-- Content (shown when expanded) -->
        <div v-if="isOpen" class="tw-overflow-auto">
            <!-- Loading state -->
            <div v-if="loading" class="tw-p-4 tw-flex tw-items-center tw-justify-center">
                <div class="tw-flex tw-items-center tw-justify-center tw-gap-2">
                    <icon icon="line-md:loading-twotone-loop" class="tw-text-primary-500 tw-text-xl" />
                    <span class="tw-text-sm tw-text-gray-500">{{ t('common.loading') }}</span>
                </div>
            </div>
            
            <!-- No results state -->
            <div v-else-if="availableOffers.length === 0" class="tw-p-4 tw-text-sm tw-text-center tw-text-gray-500">
                {{ t('messages.table-no-results') }}
            </div>
            
            <!-- Available Offers List -->
            <div v-else class="tw-px-3 tw-py-2">
                <div class="tw-divide-y tw-divide-gray-100">
                    <div v-for="offer in availableOffers" :key="offer.id" 
                        class="tw-py-2 tw-flex tw-flex-col tw-justify-between">
                        <div class="tw-flex tw-justify-between tw-items-center">
                            <div class="tw-flex tw-items-center tw-gap-2">
                                <span class="tw-bg-primary-50 tw-text-primary-600 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs">{{ offer.quantity }}×</span>
                                <span class="tw-text-sm tw-font-medium">{{ offer.product_name }}</span>
                            </div>
                            <div>
                                <span class="tw-font-semibold tw-text-sm">{{ offer.price }} {{ t('currencies.PKR') }}</span>
                            </div>
                        </div>
                        <div v-if="offer.note" class="tw-ml-8 tw-text-xs tw-text-gray-500 tw-mt-1">{{ offer.note }}</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, defineProps } from 'vue';
import { offers } from '@/api/products';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    productId: {
        type: [Number, String],
        default: null
    }
});

const { t } = useI18n();
const isOpen = ref(false); // Closed by default
const loading = ref(false);
const availableOffers = ref([]);

// Fetch available offers for a specific product
const fetchOffers = async (productId) => {
    if (!productId) {
        availableOffers.value = [];
        return;
    }
    
    loading.value = true;
    
    try {
        // Pass only the current product ID
        const response = await offers([productId]);
        if (response.data && response.data.code === 'SUCCESS' && response.data.offers) {
            availableOffers.value = response.data.offers.map(offer => ({
                id: offer.id,
                product_id: offer.product_id,
                product_name: offer.product_name || t('common.unknown-product'),
                quantity: offer.quantity || 1,
                price: offer.price || '0.00',
                note: offer.note || ''
            }));
        } else {
            availableOffers.value = [];
        }
    } catch (error) {
        console.error(t('errors.fetch-offers'), error);
        availableOffers.value = [];
    } finally {
        loading.value = false;
    }
};

// Initial fetch on component mount if productId is provided
onMounted(() => {
    if (props.productId) {
        console.log('Initial fetch for product ID:', props.productId);
        fetchOffers(props.productId);
    }
});

// Watch for changes in the productId prop to refresh data
watch(() => props.productId, (newProductId, oldProductId) => {
    if (newProductId !== oldProductId && newProductId) {
        console.log('ProductId changed, fetching offers for:', newProductId);
        fetchOffers(newProductId);
    } else if (!newProductId) {
        // Clear offers if no product ID is provided
        availableOffers.value = [];
    }
});
</script>

<style scoped>
/* Add styles for offer items */
:deep(.a-action-button.intent-primary) {
    color: #3b82f6;
}

:deep(.a-action-button.intent-primary:hover) {
    color: #2563eb;
    background-color: rgba(59, 130, 246, 0.1);
}

:deep(.a-action-button.intent-danger) {
    color: #ef4444;
}

:deep(.a-action-button.intent-danger:hover) {
    color: #dc2626;
    background-color: rgba(239, 68, 68, 0.1);
}
</style>
